import React, { useEffect } from "react";
import { GoogleMap, InfoWindow, Marker, OverlayView } from "@react-google-maps/api";
import { ColorsContext } from "../../Context/ColorsContext";
import * as Icons from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { BackButton, MapBox } from "./styles";
import { Undo } from '@mui/icons-material';
import { useTranslation } from "react-i18next";

export const MapView = ({
  mapRef,
  mapCenter,
  mapZoom,
  tableData = [],
  selected,
  activeMarkerId,
  onMarkerClick,
  onCloseInfo,
  goToDetail,
  fitBound = false,
  isDetailPage,
  handleSendFilters,
  showDetailButton
}) => {

    const { colors } = React.useContext(ColorsContext);
    const { t } = useTranslation();

    useEffect(() => {
        if (!mapRef.current) return;
        if (!tableData || tableData.length === 0) return;
        // Verificar que la librería de Google Maps esté disponible
        if (!window.google || !window.google.maps) return;

        if (fitBound) {
            const bounds = new window.google.maps.LatLngBounds();
    
            tableData.forEach(p => {
                if (p.lat != null && p.lng != null) {
                    bounds.extend({ lat: p.lat, lng: p.lng });
                }
            });

            mapRef.current.fitBounds(bounds);

            // Forzar zoom máximo cuando Google termine de ajustar
            window.google.maps.event.addListenerOnce(
                mapRef.current,
                "bounds_changed",
                () => {
                    const MAX_ZOOM = 19;
                    if (mapRef.current.getZoom() < MAX_ZOOM) {
                        mapRef.current.setZoom(MAX_ZOOM);
                    }
                }
            );
        }

    }, [tableData, mapRef, fitBound]);

    const DividerInfo = () => (
        <div style={{
            height: 1,
            background: "#eee",
            margin: "8px 0"
        }} />
    );

    const MetricInfo = ({ label, value, icon }) => {
        const IconComponent = Icons[icon];

        return (
            <div style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                padding: "6px 0",
                borderBottom: "1px solid #f2f2f2"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {IconComponent && (
                        <IconComponent style={{ fontSize: 16, color: "#777" }} />
                    )}
                    <span style={{ color: "#555", padding: "0px 2px" }}>{label}:</span>
                </div>
                
                <strong>{value}</strong>
            </div>
        )
    }

    const RiskDotInfo = ({ color, label, value, isActive }) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: color
            }} />
            <span style={{ fontWeight: isActive ? 700 : 400 }}>
                {label} ({value})
            </span>
        </div>
    );

    const getRiskLevel = (detail) => {
        const { highPermits = 0, mediumPermits = 0, lowPermits = 0 } = detail || {};

        const max = Math.max(highPermits, mediumPermits, lowPermits);

        if (max === highPermits && max > 0) return "high";
        if (max === mediumPermits && max > 0) return "medium";
        return "low";
    };

    const getRiskColor = (level) => {
        switch (level) {
            case "high":
                return "#EB5757";
            case "medium":
                return "#F2C94C";
            default:
                return "#6FCF97";
        }
    };

    return (
        <MapBox>
            <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={mapCenter}
                zoom={mapZoom}
                onLoad={map => (mapRef.current = map)}
                options={{
                    mapTypeId: "satellite",
                    disableDefaultUI: true, // quita todo
                    mapTypeControl: true, // vuelve a activar el mapType
                    streetViewControl: false,
                    fullscreenControl: false
                }}
            >
                {/* Markers */}
                {tableData.map((item) => {
                    // Verificar que Google Maps esté completamente cargado antes de usar SymbolPath
                    const hasGoogleMaps = window.google && window.google.maps && window.google.maps.SymbolPath;

                    const riskLevel = getRiskLevel(item.detail);
                    const riskColor = getRiskColor(riskLevel);

                    // Crear un SVG string a partir del icono de Material y aplicarle fill
                    const svgMarker = `
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="${riskColor}">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                        <circle cx="12" cy="9" r="2.5" fill="white"/>
                        </svg>
                    `;
                    
                    return (
                        <Marker
                            key={item.id}
                            position={{ lat: item.lat, lng: item.lng }}
                            onClick={onMarkerClick ? () => onMarkerClick(item) : undefined}
                            label={{
                                text: String(item.index),
                                color: "#000",
                                fontSize: "14px",
                                fontWeight: "bold",
                            }}
                            icon={{
                                url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svgMarker),
                                scaledSize: new window.google.maps.Size(40, 40),
                                labelOrigin: new window.google.maps.Point(20, 15)
                            }}
                        />
                    );
                })}

                {/* Info marker */}
                {selected && (
                    <InfoWindow
                        position={{ lat: selected.lat, lng: selected.lng }}
                        onCloseClick={onCloseInfo}
                    >
                        <div
                            style={{
                                minWidth: 260,
                                borderRadius: 12,
                                background: "#fff",
                                padding: 12,
                                fontSize: 13,
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                            }}
                        >
                            {/* HEADER */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 8
                                }}
                            >
                                <strong style={{ fontSize: 15 }}>
                                    {selected.name}
                                </strong>

                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    {showDetailButton && (
                                        <button
                                            style={{
                                                background: "#1E3A5F",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: 6,
                                                padding: "4px 10px",
                                                cursor: "pointer",
                                                fontSize: 12
                                            }}
                                            onClick={() => goToDetail(selected)}
                                        >
                                            {t("map.see")}
                                        </button>
                                    )}

                                    <span
                                        style={{ cursor: "pointer", fontWeight: "bold" }}
                                        onClick={onCloseInfo}
                                    >
                                        ✕
                                    </span>
                                </div>
                            </div>

                            {/* NIVEL DE RIESGO */}
                            <div style={{ marginBottom: 10 }}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                    {t("map.riskLevel")}
                                </div>

                                {(() => {
                                    const riskLevelDotInfo = getRiskLevel(selected.detail);

                                    return (
                                        <div style={{ display: "flex", gap: 12 }}>
                                            <RiskDotInfo color="#6FCF97" label={t("map.low")} value={selected.detail.lowPermits} isActive={riskLevelDotInfo === "low"}/>
                                            <RiskDotInfo color="#F2C94C" label={t("map.medium")} value={selected.detail.mediumPermits} isActive={riskLevelDotInfo === "medium"}/>
                                            <RiskDotInfo color="#EB5757" label={t("map.high")} value={selected.detail.highPermits} isActive={riskLevelDotInfo === "high"}/>
                                        </div>
                                    )
                                })()}

                            </div>

                            <DividerInfo />

                            {/* METRICAS */}
                            <MetricInfo icon="Description" label={t("map.activePermits")} value={selected.detail.workpermits} />
                            <MetricInfo icon="Groups" label={t("map.fieldWorkers")} value={selected.detail.fieldWorkers} />
                            <MetricInfo icon="AssignmentLate" label={t("map.suspendedPermits")} value={selected.detail.suspendedPermits} />
                            <MetricInfo icon="Air" label={t("map.atmosphereMonitoring")} value={selected.detail.gasesPermits} />
                            <MetricInfo icon="Lock" label={t("map.activeLotos")} value={selected.detail.lotoPermits} />
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
            {isDetailPage && (
                <BackButton
                    variant="contained"
                    aria-label="close"
                    type="close"
                    startIcon={<Undo />}
                    onClick={handleSendFilters}
                    sx={{
                        position: "absolute",
                        top: 10,
                        right: 16,
                        zIndex: 10
                    }}
                >
                    {t("map.return")}
                </BackButton>
            )}
        </MapBox>
    )
}
